/**
 * Bid Control Room — Proposal workspace: print, readiness, and clear next actions.
 */
import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  List,
  Row,
  Space,
  Steps,
  Tag,
  Typography,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { EpcCommercialProposalCockpit } from './EpcCommercialProposalCockpit';
import { EpcTechnicalProposalCockpit } from './EpcTechnicalProposalCockpit';
import {
  buildProposalReadiness,
  readinessTagColor,
} from './estimateProposalSubmissionReadiness';
import { openEpcProposalPrint, openLinkedEstimatePrint } from '@/utils/openEpcProposalPrint';

const { Text, Title, Paragraph } = Typography;

export type EpcProposalWorkspaceProps = {
  tenderId: string;
  tender: Record<string, unknown>;
  proposal: Record<string, unknown>;
  onToggleTechSection?: (key: string, complete: boolean) => void;
  techPending?: boolean;
  onGoSubmission?: () => void;
  onGoEstimate?: () => void;
  onCreateSubmission?: () => void;
  createSubmissionPending?: boolean;
  hasSubmission?: boolean;
};

export const EpcProposalWorkspace: React.FC<EpcProposalWorkspaceProps> = ({
  tenderId,
  tender,
  proposal,
  onToggleTechSection,
  techPending,
  onGoSubmission,
  onGoEstimate,
  onCreateSubmission,
  createSubmissionPending,
  hasSubmission,
}) => {
  const id = String(proposal.id || '');
  const readiness = buildProposalReadiness(proposal);
  const meta = (proposal.meta_json || proposal.metaJson || {}) as Record<string, unknown>;
  const estPin = (meta.est3_pricing_pin || meta.est3PricingPin || null) as Record<
    string,
    unknown
  > | null;
  const currency = String(proposal.currency || tender.currency || 'AED');
  const bidValue = Number(proposal.commercial_value ?? proposal.commercialValue);

  const basisQ = useQuery({
    queryKey: ['epc-proposal-submission-basis', id],
    queryFn: () => epcCommercialService.getProposalSubmissionBasis(id),
    enabled: !!id,
  });
  const basisStatus = String((basisQ.data as any)?.status || (estPin ? 'CERTIFIED' : 'NOT_RECORDED'));

  const estimateId = String(
    proposal.estimation_id || proposal.estimationId || estPin?.estimate_id || ''
  );
  const revisionId = String(estPin?.estimate_revision_id || '');

  const estimatesQ = useQuery({
    queryKey: ['epc-estimates', tenderId],
    queryFn: () => epcEstimationService.list(tenderId),
    enabled: !!tenderId && !estimateId,
  });
  const fallbackEstimateId =
    estimateId || (estimatesQ.data?.[0]?.id ? String(estimatesQ.data[0].id) : '');

  const nextSteps = useMemo(() => {
    const steps: Array<{ title: string; description: string; done: boolean }> = [
      {
        title: 'Complete technical sections',
        description: 'Mark mandatory technical proposal sections complete',
        done: readiness.checks.find((c) => c.id === 'prop-tech')?.state === 'READY',
      },
      {
        title: 'Set commercial bid value & terms',
        description: 'Confirm bid price, payment terms, and deviations',
        done:
          readiness.checks.find((c) => c.id === 'prop-value')?.state === 'READY' ||
          (Number.isFinite(bidValue) && bidValue > 0),
      },
      {
        title: 'Certify Approved EST3 estimate basis',
        description: 'Pin an Approved estimate revision — required before submission',
        done: basisStatus === 'CERTIFIED',
      },
      {
        title: 'Open Submission Control',
        description: 'Assemble pack, record customer submission, and submit for approval',
        done: !!hasSubmission,
      },
    ];
    return steps;
  }, [readiness.checks, bidValue, basisStatus, hasSubmission]);

  const currentStep = nextSteps.findIndex((s) => !s.done);
  const nextAction = currentStep >= 0 ? nextSteps[currentStep] : null;

  const printProposal = () => {
    openEpcProposalPrint({
      tender,
      proposal,
      basisStatus,
      estimateNumber: estPin?.estimate_number ? String(estPin.estimate_number) : null,
      revisionLabel: estPin?.revision_label ? String(estPin.revision_label) : null,
    });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card size="small">
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={14}>
            <Title level={4} style={{ margin: 0 }}>
              Proposal workspace
            </Title>
            <Space wrap style={{ marginTop: 8 }}>
              <Tag color={readinessTagColor(readiness.overall)}>{readiness.overall}</Tag>
              <Tag color={basisStatus === 'CERTIFIED' ? 'green' : basisStatus === 'STALE' ? 'red' : 'orange'}>
                Basis {basisStatus}
              </Tag>
              <Tag>{String(proposal.status || 'Draft')}</Tag>
              <Text>
                Bid{' '}
                {Number.isFinite(bidValue)
                  ? formatMoney(bidValue, { currency })
                  : '—'}
              </Text>
            </Space>
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              Build the customer-facing proposal here, print packs, then move to Submission when
              technical + commercial + estimate basis are ready.
            </Paragraph>
          </Col>
          <Col xs={24} md={10}>
            <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button icon={<PrinterOutlined />} onClick={printProposal}>
                Print proposal
              </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={() =>
                  void openLinkedEstimatePrint({
                    estimateId: fallbackEstimateId || estimateId,
                    revisionId: revisionId || null,
                    tenderTitle: String(tender.title || tender.tender_number || ''),
                  })
                }
              >
                Print estimate
              </Button>
              {onGoEstimate ? (
                <Button onClick={onGoEstimate}>Open estimate</Button>
              ) : null}
            </Space>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="What to do next">
        <Steps
          direction="vertical"
          size="small"
          current={currentStep < 0 ? nextSteps.length : currentStep}
          items={nextSteps.map((s) => ({
            title: s.title,
            description: s.description,
            status: s.done ? 'finish' : undefined,
          }))}
        />
        {nextAction ? (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message={`Next: ${nextAction.title}`}
            description={nextAction.description}
            action={
              <Space>
                {currentStep <= 2 && onGoEstimate ? (
                  <Button size="small" type="primary" onClick={onGoEstimate}>
                    {currentStep === 2 ? 'Certify on Estimate / below' : 'Open Estimate'}
                  </Button>
                ) : null}
                {currentStep >= 3 && !hasSubmission && onCreateSubmission ? (
                  <Button
                    size="small"
                    type="primary"
                    loading={createSubmissionPending}
                    onClick={onCreateSubmission}
                  >
                    Create submission
                  </Button>
                ) : null}
                {hasSubmission && onGoSubmission ? (
                  <Button size="small" type="primary" onClick={onGoSubmission}>
                    Open Submission
                  </Button>
                ) : null}
              </Space>
            }
          />
        ) : (
          <Alert
            style={{ marginTop: 12 }}
            type="success"
            showIcon
            message="Proposal checklist complete"
            description="Continue in Submission Control to assemble the pack and record customer submission."
            action={
              onGoSubmission ? (
                <Button type="primary" onClick={onGoSubmission}>
                  Open Submission
                </Button>
              ) : (
                onCreateSubmission && (
                  <Button type="primary" loading={createSubmissionPending} onClick={onCreateSubmission}>
                    Create submission
                  </Button>
                )
              )
            }
          />
        )}
        <List
          size="small"
          style={{ marginTop: 8 }}
          header={<Text strong>Readiness checks</Text>}
          dataSource={readiness.checks}
          renderItem={(c) => (
            <List.Item>
              <Tag color={c.state === 'READY' ? 'green' : c.state === 'BLOCKED' ? 'red' : 'orange'}>
                {c.state}
              </Tag>
              <Text>
                {c.label}
                {c.detail ? ` — ${c.detail}` : ''}
              </Text>
            </List.Item>
          )}
        />
      </Card>

      <EpcTechnicalProposalCockpit
        proposal={proposal}
        onToggleSection={onToggleTechSection}
        pending={techPending}
      />
      <EpcCommercialProposalCockpit tenderId={tenderId} proposal={proposal} />
    </Space>
  );
};

export default EpcProposalWorkspace;
